import { useEffect, useState } from "react";
import {
  useAppKitAccount,
  useAppKit,
  useDisconnect,
} from "@reown/appkit/react";
import { motion } from "framer-motion";
import { CheckCircle, Copy, Loader2, LogIn, LogOut } from "lucide-react";

export interface WalletProps {
  setUserAddress: React.Dispatch<React.SetStateAction<string | null>>;
  setBalanceRefresh: React.Dispatch<React.SetStateAction<number>>;
}

const Wallet: React.FC<WalletProps> = ({
  setUserAddress,
  setBalanceRefresh,
}) => {
  const { address } = useAppKitAccount({ namespace: "eip155" });
  const { open } = useAppKit();
  const { disconnect } = useDisconnect();

  const [localAddress, setLocalAddress] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  useEffect(() => {
    if (!address) return;
    setUserAddress(address);
    setLocalAddress(address);
    setBalanceRefresh((prev) => prev + 1);
    localStorage.setItem("userAddress", address);
  }, [address, setUserAddress, setBalanceRefresh]);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      await open();
    } catch (err) {
      console.error("[Wallet] Connect error:", err);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await disconnect();
      localStorage.clear();
      sessionStorage.clear();
      setUserAddress(null);
      setLocalAddress(null);
      window.postMessage({ type: "DISCONNECT_FROM_DAPP" }, "*");
      window.location.reload();
    } catch (err) {
      console.error("[Wallet] Disconnect error:", err);
    } finally {
      setIsDisconnecting(false);
    }
  };

  const copyAddress = async () => {
    if (!localAddress) return;
    if (!navigator.clipboard) {
      console.error("[Wallet] Clipboard API non disponible");
      return;
    }
    try {
      await navigator.clipboard.writeText(localAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("[Wallet] Erreur lors de la copie :", err);
    }
  };

  const isWalletConnected = Boolean(localAddress);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[rgba(3,44,88,0.5)] backdrop-blur-md p-2 shadow-lg border border-white/20 text-white text-xs"
    >
      {isWalletConnected ? (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative bg-[rgba(0,20,40,0.7)] p-3 border border-white/10"
        >
          <motion.button
            type="button"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleDisconnect}
            disabled={isDisconnecting}
            className="absolute top-1.5 right-1.5 p-1 bg-red-600/50 hover:bg-red-500/70 rounded-full transition disabled:opacity-50 disabled:cursor-wait"
            aria-label="Disconnect Wallet"
          >
            {isDisconnecting ? (
              <Loader2 size={16} className="animate-spin text-white" />
            ) : (
              <LogOut size={16} className="text-white" />
            )}
          </motion.button>

          <p className="text-[10px] text-gray-400 mb-2 font-bold uppercase tracking-wide mt-2">
            Your Address:
          </p>
          <div className="flex gap-3 items-center justify-between">
            <a
              href={`https://basescan.org/address/${address}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-700 hover:text-green-500 break-all font-mono text-sm leading-tight bg-black/20 py-1 select-all text-center shadow-md decoration-0"
              title={localAddress ?? ""}
            >
              {localAddress ?? "N/A"}
            </a>
            <motion.button
              type="button"
              whileTap={{ scale: 0.98 }}
              whileHover={{ scale: 1.03 }}
              onClick={copyAddress}
              disabled={copied}
              className={`flex items-center justify-center gap-1 text-[10px] px-1 rounded transition ${
                copied
                  ? "bg-green-700/80 cursor-default"
                  : "bg-white/20 hover:bg-white/30"
              }`}
            >
              {copied ? <CheckCircle size={14} /> : <Copy size={14} />}
            </motion.button>
          </div>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center gap-2"
        >
          <p className="text-center font-bold text-[11px] uppercase mb-1 text-[var(--color-primary-light)] tracking-wider">
            Connect your wallet to access the dashboard.
          </p>
          <motion.button
            type="button"
            whileTap={{ scale: 0.96 }}
            whileHover={{ scale: 1.02 }}
            onClick={handleConnect}
            disabled={isConnecting || isDisconnecting}
            className="w-full py-1.5 text-[11px] flex items-center justify-center gap-1.5 font-semibold bg-[rgba(200,0,0,0.6)] hover:bg-[rgba(241,3,3,0.8)] transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isConnecting ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Connecting...
              </>
            ) : (
              <>
                <LogIn size={14} /> Connect Wallet
              </>
            )}
          </motion.button>
          {isDisconnecting && (
            <p className="text-[10px] text-red-400 animate-pulse mt-1">
              Disconnecting...
            </p>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

export default Wallet;
